import { PURCHASER_API_BASE_URL } from "../../../clients/Constants.ts";
import logoWhite from "../../../assets/images/logo_white.svg";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import ComparisonBlocks from "./Blocks/ComparisonBlocks.tsx";
import documentStack from "../../../assets/images/document_stack.svg";
import Button from "../../DesignSystem/Button.tsx";
import "../compare-generic.css";

/**
 * Public shared comparison view
 * This is an unprotected page that renders a comparison using a share key in the path
 */
export default function PublicViewComparisonPage() {
    const params = useParams<{ shareKey: string }>();

    const query = useQuery({
        queryKey: ["comparison", "public", params.shareKey],
        queryFn: () => {
            return fetch(`${PURCHASER_API_BASE_URL}/comparisons/public/${params.shareKey}`)
                .then(res => res.json())
                .then(data => {
                    // put the line items block at the end
                    const lineItemsIndex = data.blocks.findIndex(block => block.type === "Line Items");
                    const lineItemsBlock = data.blocks[lineItemsIndex];
                    data.blocks.splice(lineItemsIndex, 1);
                    data.blocks.push(lineItemsBlock);
                    return data;
                });
        },
        retry: false,
    });

    return (
        <div className="compare-page-wrapper bg-stone-100">
            {/* noindex meta tag so that these (in theory) don't get indexed */}
            <meta name="robots" content="noindex" />

            <div className="flex flex-col items-center justify-between gap-6 bg-stone-950 px-10 py-4 md:flex-row">
                <a href="https://purchaser.ai">
                    <img alt="Purchaser Logo" src={logoWhite} className="" />
                </a>
                <div className="flex items-center gap-4">
                    <a href="/login" className="rounded-xl border border-stone-400 px-2 py-1 font-medium text-white">
                        Sign In
                    </a>
                    <a href="/login" className="border-bronze-500 bg-bronze-600 rounded-xl border px-2 py-1 font-medium text-white">
                        Start for Free
                    </a>
                </div>
            </div>
            <div className="bg-bronze-200 px-6 py-3 text-sm">
                By viewing this public link, you agree to Purchaser's{" "}
                <a className="font-medium underline" href="https://purchaser.ai/policy/terms-and-conditions" target="_blank">
                    Terms
                </a>{" "}
                and{" "}
                <a className="font-medium underline" href="https://purchaser.ai/policy/privacy-policy" target="_blank">
                    Privacy Policy
                </a>
                . Log in to view the documents used for this report. If you’re new to Purchaser, sign up for a free account to start your own quote
                comparison.
            </div>
            {query.isError && (
                <div className="mx-auto flex h-[calc(100vh-220px)] max-w-xl flex-col items-center justify-center px-4 text-center">
                    <img alt="Not Found" src={documentStack} />
                    <h1 className="mt-4 text-xl font-normal">Sorry, we couldn't find that comparison</h1>
                    <p className="mt-2 text-stone-700">
                        Check with the person who shared this link to make sure it's still valid. You can also sign up for a free account to start
                        your own quote comparison.
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-2">
                        <Button color="bronze" text="Book a Demo" href="https://purchaser.ai/book-a-demo" />
                        <Button color="white" text="Learn More" href="https://purchaser.ai" />
                    </div>
                </div>
            )}
            {!query.isError && query.data && (
                <div className="mx-auto mt-6 max-w-7xl rounded-xl bg-white py-8">
                    <ComparisonBlocks comparison={query.data} />
                </div>
            )}
        </div>
    );
}
